import React from 'react'
import { FC } from 'react'
import { graphql, Link, useStaticQuery } from 'gatsby'
import SvgIcon from '../SVGIcon/SvgIcon'
import DottedRectangles from '../DottedRectangles/DottedRectangles'
import { cls } from '../../utils/utils'
import { IFooterData, IFooterTemplateData, IFooterComponentData, IFooterSocialNetwork } from './Footer.types'
import styles from './Footer.module.scss'
import { footerLinkColumns } from './Footer.constants'

const Footer: FC<{ isDark?: boolean; dottedRectanglesClassName?: string; className?: string }> = ({
  dottedRectanglesClassName,
  className = '',
}) => {
  const data = useStaticQuery<IFooterData>(query)
  return <FooterTemplate {...data.footer.frontmatter} dottedRectanglesClassName={dottedRectanglesClassName} className={className} />
}

const getCopyrightText = (): string => {
  const currentYear = new Date().getFullYear()
  return `© 2005—${currentYear} DSR Corporation`
}

export const SocialNetworksComponent: FC<IFooterComponentData> = ({ socialNetworkText, socialNetworks }) => {
  return (
    <div className={styles.socialNetworksWrapper}>
      <p className={styles.socialNetworkText}>{socialNetworkText}</p>
      <div className={styles.socialNetworks}>
        {socialNetworks.map((network: IFooterSocialNetwork) => (
          <a href={network.link} key={network.altText} target="_blank" rel="noopener">
            {network.icon?.publicURL && (
              <SvgIcon href={network.icon?.publicURL} className={cls(styles.socialNetworkImage, 'mobile-hide')} inline />
            )}
            {network.mobileIcon?.publicURL && (
              <SvgIcon href={network.mobileIcon?.publicURL} className={cls(styles.socialNetworkImage, 'desktop-hide')} inline />
            )}
          </a>
        ))}
      </div>
    </div>
  )
}

export const LinkColumnComponent: FC<{ links: { name: string; path: string }[] }> = ({ links }) => {
  return (
    <div className={styles.column}>
      {links.map((link) => (
        <Link to={link.path} className={styles.pageLink} key={link.path}>
          {link.name}
        </Link>
      ))}
    </div>
  )
}

export const LinksComponent: FC<Record<string, never>> = () => {
  return (
    <div className={styles.links}>
      {footerLinkColumns.map((column) => (
        <LinkColumnComponent links={column} key={column[0]?.path} />
      ))}
    </div>
  )
}

export const FooterComponent: FC<IFooterComponentData> = ({ dottedRectanglesClassName, ...data }) => {
  return (
    <div className={cls('main-container')}>
      <div className={styles.darkPlaceholder} />
      <div className={styles.mainFooterContent}>
        <LinksComponent />
        <SocialNetworksComponent {...data} />
      </div>
      <p className={styles.copyright}>{getCopyrightText()}</p>
      <DottedRectangles className={cls(styles.firstRectangles, dottedRectanglesClassName, 'mobile-hide')} />
      <DottedRectangles className={cls(styles.secondRectangles, dottedRectanglesClassName, 'mobile-hide')} />
      <DottedRectangles className={cls(styles.thirdRectangles, dottedRectanglesClassName, 'mobile-hide')} />
    </div>
  )
}

export const FooterTemplate: FC<IFooterTemplateData & { dottedRectanglesClassName?: string; className?: string }> = ({
  dottedRectanglesClassName,
  className,
  ...data
}) => {
  return (
    <footer className={cls(styles.footer, className)}>
      <FooterComponent {...data} dottedRectanglesClassName={dottedRectanglesClassName} />
    </footer>
  )
}

export const query = graphql`
  query FooterData {
    footer: markdownRemark(frontmatter: { componentName: { eq: "Footer" } }) {
      frontmatter {
        socialNetworks {
          altText
          icon {
            publicURL
          }
          mobileIcon {
            publicURL
          }
          link
        }
        socialNetworkText
      }
    }
  }
`

export default Footer
